import { useTranslation } from "react-i18next";
import { Tile } from "@carbon/react";
import { Document, Box, Enterprise } from "@carbon/icons-react";
import StatCard from "../../components/carbon/StatCard/StatCard";
import { OrderResponse } from "../../types";
import { formatDate, orderPlace, orderPlaceLabelKey } from "../../utils";
import "./bid.scss";

type OrderConditionsTileProps = {
  order?: OrderResponse;
};

/**
 * The buyer-conditions panel (company/buyer name/payment/supply period/delivery place) — shared
 * by `BuyerBid.tsx` and `JointBuyerBid.tsx` (Task 7): both pass a plain `OrderResponse` (the
 * single order, or `jointOrder[0]` for the joint case — every field this reads lives on that one
 * shape either way), so one Carbon `Tile` component replaces what used to be two near-identical
 * `renderOrderInformation` closures.
 */
export const OrderConditionsTile = ({ order }: OrderConditionsTileProps) => {
  const { t } = useTranslation();

  return (
    <Tile className="bid-tile">
      <h3 className="bid-tile__title">{t("offer.buyerConditions")}</h3>
      <div className="bid-tile__info-grid">
        <div className="bid-tile__info-item">
          <span className="bid-tile__info-label">
            {t("offer.buyerCompany")}
          </span>
          <p className="bid-tile__info-value">{order?.company?.name}</p>
        </div>
        <div className="bid-tile__info-item">
          <span className="bid-tile__info-label">{t("offer.buyerName")}</span>
          <p className="bid-tile__info-value">{order?.user?.fullName}</p>
        </div>
        <div className="bid-tile__info-item">
          <span className="bid-tile__info-label">
            {t("tenders.form.paymentMethod")}
          </span>
          <p className="bid-tile__info-value">
            {order?.paymentMethod} - {order?.paymentTerm} {t("analytics.days")}
          </p>
        </div>
        <div className="bid-tile__info-item">
          <span className="bid-tile__info-label">
            {t("offer.buyerSupplyPeriod")}
          </span>
          <p className="bid-tile__info-value">
            {order?.startSupplyDate === order?.endSupplyDate ? (
              formatDate(order?.startSupplyDate ?? "")
            ) : (
              <>
                {formatDate(order?.startSupplyDate ?? "")} -{" "}
                {formatDate(order?.endSupplyDate ?? "")}
              </>
            )}
          </p>
        </div>
        <div className="bid-tile__info-item">
          <span className="bid-tile__info-label">
            {t(orderPlaceLabelKey(order))}
          </span>
          <p className="bid-tile__info-value">{orderPlace(order)}</p>
        </div>
      </div>
    </Tile>
  );
};

type OfferSummaryTileProps = {
  totalOffers: number;
  materialsCount: number;
  suppliersCount: number;
};

/**
 * The three-stat "Offer Summary" tile — the e2e spec's `getByText("Total Offers")` anchor lives
 * here (`e2e/orders-bidding.spec.ts`'s BuyerBid test). Both flows compute their own
 * `materialsCount`/`suppliersCount` (1 material for a SIMPLE order vs. `jointOrder.length` for a
 * JOINT one; supplier counts differ similarly) and pass plain numbers in, so this stays a dumb
 * presentational tile shared by both.
 */
export const OfferSummaryTile = ({
  totalOffers,
  materialsCount,
  suppliersCount,
}: OfferSummaryTileProps) => {
  const { t } = useTranslation();

  return (
    <div className="bid-summary-metrics">
      <StatCard
        label={t("tenders.data.totalOffers")}
        value={totalOffers}
        icon={Document}
      />
      <StatCard
        label={t("tenders.data.materialsCount")}
        value={materialsCount}
        icon={Box}
      />
      <StatCard
        label={t("tenders.data.suppliersCount")}
        value={suppliersCount}
        icon={Enterprise}
      />
    </div>
  );
};
